import { useCallback, useEffect, useState } from "react";
import { Activity, ClipboardList, Receipt, LayoutGrid, Ban } from "lucide-react";
import { api, fmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { Card, inp } from "@/components/ui-kit";
import { useRealtime } from "@/hooks/useRealtime";

const icons = { order_created: ClipboardList, order_items_added: ClipboardList, payment: Receipt, table_status: LayoutGrid, tables_moved: LayoutGrid, order_cancelled: Ban };
const hm = (iso) => new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

export function ActivityLog({ staff }) {
  const { restaurant } = useAuth();
  const { t } = useT();
  const rid = restaurant.id;
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [uid, setUid] = useState("");
  const [data, setData] = useState({ events: [], summary: [] });
  const load = useCallback(() => api.get(`/restaurants/${rid}/activity`, { params: { date, user_id: uid || undefined } }).then((r) => setData(r.data)), [rid, date, uid]);
  useEffect(() => { load(); }, [load]);
  useRealtime(rid, load, ["orders", "tables", "shifts"]);
  return (
    <Card className="mt-6" data-testid="activity-card">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-2 font-heading text-lg font-bold"><Activity size={18} className="text-[#4a8a56]" />{t("activity_log")}</p>
        <div className="flex items-center gap-2">
          <select data-testid="activity-user-select" className={inp + " w-auto"} value={uid} onChange={(e) => setUid(e.target.value)}><option value="">{t("all_staff")}</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
          <input data-testid="activity-date-input" type="date" className={inp + " w-auto"} value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
      </div>
      {data.summary.length > 0 && <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {data.summary.map((s) => <div key={s.user_id} data-testid={`activity-summary-${s.user_name}`} className="rounded-xl border border-slate-100 bg-slate-50 p-3 text-sm">
          <p className="font-semibold">{s.user_name} <span className="text-xs font-normal text-slate-500">· {t(s.staff_role)}</span></p>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600">
            <span>{t("orders_count")}: <b>{s.orders}</b></span><span>{t("payments_count")}: <b>{s.payments}</b></span><span>{t("revenue")}: <b>{fmt(s.revenue)}</b></span>
            {s.tables.length > 0 && <span>{t("serving_tables")}: <b>{s.tables.join(", ")}</b></span>}
          </div>
        </div>)}
      </div>}
      {data.events.length === 0 && <p className="py-3 text-sm text-slate-500" data-testid="no-activity">{t("no_activity")}</p>}
      <div className="max-h-96 divide-y divide-slate-100 overflow-y-auto">
        {data.events.map((e) => { const I = icons[e.kind] || Activity; return (
          <div key={e.id} data-testid={`activity-row-${e.id}`} className="flex items-center gap-3 py-2 text-sm">
            <span className="w-12 shrink-0 font-mono text-xs text-slate-500">{hm(e.created_at)}</span>
            <I size={14} className={e.kind === "payment" ? "text-emerald-600" : e.kind === "order_cancelled" ? "text-rose-500" : "text-[#4a8a56]"} />
            <span className="min-w-0 flex-1 truncate"><b>{e.user_name}</b> <span className="text-slate-600">· {e.detail}</span></span>
            {e.amount > 0 && <span className="shrink-0 font-semibold">{fmt(e.amount)}</span>}
          </div>); })}
      </div>
    </Card>
  );
}
